import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, Tag, Users, Settings as SettingsIcon, Ticket, Inbox, FileText } from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";

export default function AdminShell({ children }) {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const linkCls = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition ${
      isActive ? "bg-oj-ink text-white" : "text-zinc-600 hover:text-oj-ink hover:bg-zinc-100"
    }`;

  async function onLogout() {
    await logout();
    navigate("/admin", { replace: true });
  }

  return (
    <div data-testid="admin-shell" className="min-h-screen bg-zinc-50">
      <div className="container-oj py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-oj-brand font-semibold">
              O&#39;javana Admin
            </div>
            <h1 className="mt-1 text-3xl font-display font-black tracking-tight text-oj-ink">
              Admin
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Signed in as <span className="font-semibold text-oj-ink">{admin?.email}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/offers"
              target="_blank"
              className="text-sm px-3 py-2 rounded-md border border-zinc-300 hover:border-oj-ink"
            >
              View public offers
            </Link>
            <button
              type="button"
              data-testid="admin-logout-button"
              onClick={onLogout}
              className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-zinc-300 hover:border-red-500 hover:text-red-600"
            >
              <LogOut className="size-4" /> Sign out
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
          <aside className="rounded-2xl border border-zinc-200 bg-white p-3 h-fit sticky top-24">
            <nav className="flex flex-col gap-1">
              <NavLink to="/admin" end data-testid="admin-nav-inbox" className={linkCls}>
                <Inbox className="size-4" /> Inbox
              </NavLink>
              <NavLink to="/admin/offers" data-testid="admin-nav-offers" className={linkCls}>
                <Tag className="size-4" /> Offers
              </NavLink>
              <NavLink to="/admin/pages" data-testid="admin-nav-pages" className={linkCls}>
                <FileText className="size-4" /> Pages
              </NavLink>
              <NavLink to="/admin/settings" data-testid="admin-nav-settings" className={linkCls}>
                <SettingsIcon className="size-4" /> Settings
              </NavLink>
              <NavLink to="/admin/vouchers" data-testid="admin-nav-vouchers" className={linkCls}>
                <Ticket className="size-4" /> Vouchers
              </NavLink>
              <NavLink to="/admin/leads" data-testid="admin-nav-leads" className={linkCls}>
                <Users className="size-4" /> Leads
              </NavLink>
            </nav>
          </aside>
          <main className="min-w-0">{children}</main>
        </div>
      </div>
    </div>
  );
}
